import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from '../config';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  People as PeopleIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { companiesAPI as companyAPI } from '../services';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../utils/userPermissions';
import { getCompanyTypeLabel, getCompanyTypeColor } from '../utils/companyTypes';
import { getTimezoneAbbreviation } from '../utils/timezones';
import EmptyState from '../components/EmptyState';
import {
  HEADER_CELL_SX,
  ListToolbar,
  NameCell,
  StatusDot,
  RowActionsMenu,
  DetailStrip,
  SelectableTableRow,
  SelectableCard,
  useRowSelection,
  type RowAction,
} from '../components/ListPageShared';

interface Company {
  companyid: string;
  companyname: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  phone: string;
  email: string;
  website: string;
  contactperson: string;
  isactive: boolean;
  createtime: string;
  timezone: string;
  companytype: string;
  prefixword: string;
  reportprefix: string;
  reportno: string;
  logo?: string;
}

type StatusFilterKey = 'all' | 'active' | 'inactive';

const Companies: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [companyTypeFilter, setCompanyTypeFilter] = useState<string>('All');
  const [statusFilter, setStatusFilter] = useState<StatusFilterKey>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const selection = useRowSelection();
  // Delete confirmation dialog state
  const [deleteTarget, setDeleteTarget] = useState<Company | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Check if user is super admin using global utility
  const userIsSuperAdmin = isSuperAdmin(user);

  useEffect(() => {
    if (userIsSuperAdmin) {
      fetchCompanies();
    } else {
      setLoading(false);
      setError(t('companies.accessDenied'));
    }
  }, [userIsSuperAdmin]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch companies from the real API (page is super-admin gated, so the
      // super admin company itself is shown too)
      const response = await companyAPI.getCompanies();
      setCompanies(response);
    } catch (err: any) {
      setError(t('companies.fetchError'));
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  };

  // Search and type apply first so the status counts follow them
  const searchedCompanies = useMemo(() => {
    const term = searchText.toLowerCase();
    return companies.filter((company) => {
      const matchesType = companyTypeFilter === 'All' || company.companytype === companyTypeFilter;
      const matchesSearch =
        !term ||
        [company.companyname, company.contactperson, company.email, company.city, company.country, company.prefixword]
          .some((v) => v?.toLowerCase().includes(term));
      return matchesType && matchesSearch;
    });
  }, [companies, searchText, companyTypeFilter]);

  const activeCount = searchedCompanies.filter((c) => c.isactive).length;

  const filteredCompanies = useMemo(() => {
    if (statusFilter === 'all') return searchedCompanies;
    return searchedCompanies.filter((c) => c.isactive === (statusFilter === 'active'));
  }, [searchedCompanies, statusFilter]);

  // Keep the page in range when filters shrink the list
  const lastPage = Math.max(0, Math.ceil(filteredCompanies.length / rowsPerPage) - 1);
  const currentPage = Math.min(page, lastPage);
  const paginatedCompanies = filteredCompanies.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

  // Reset to the first page when search or filters change
  useEffect(() => {
    setPage(0);
  }, [searchText, companyTypeFilter, statusFilter]);

  const handleEdit = (companyId: string) => {
    navigate(`/companies/${companyId}/edit`);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await companyAPI.deleteCompany(deleteTarget.companyid);
      setSnackbar({ msg: t('companies.deleted', 'Company deactivated'), severity: 'success' });
      setDeleteTarget(null);
      fetchCompanies();
    } catch (e: any) {
      setSnackbar({
        msg: e.response?.data?.detail || t('companies.deleteFailed', 'Failed to delete company'),
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleView = (companyId: string) => {
    navigate(`/companies/${companyId}`);
  };

  const handleViewUsers = (companyId: string) => {
    navigate(`/companies/${companyId}/users`);
  };

  const handleAddCompany = () => {
    navigate('/companies/new');
  };

  const handleCompanyTypeFilterChange = (event: SelectChangeEvent) => {
    setCompanyTypeFilter(event.target.value);
  };

  // -------------------------------------------------------------------------
  // Shared render fragments
  // -------------------------------------------------------------------------

  const companyAvatar = (company: Company, size = 34) => {
    const logoPath = company.logo && company.logo.trim()
      ? `${config.apiBaseUrl}/files/company-logos/${company.companyid}/${company.logo}`
      : undefined;
    return (
      <Avatar
        src={logoPath}
        alt={`${company.companyname} logo`}
        sx={{ width: size, height: size, bgcolor: 'primary.main', fontSize: size * 0.4, border: '1px solid', borderColor: 'grey.300' }}
        imgProps={{ style: { objectFit: 'contain' } }}
      >
        {company.companyname ? company.companyname.charAt(0).toUpperCase() : '?'}
      </Avatar>
    );
  };

  const typeChip = (company: Company) => (
    <Chip
      label={getCompanyTypeLabel(t, company.companytype)}
      color={getCompanyTypeColor(company.companytype)}
      variant="outlined"
      size="small"
    />
  );

  const nameCell = (company: Company, size?: number) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      {companyAvatar(company, size)}
      <NameCell name={company.companyname} subtitle={company.contactperson || company.email || '—'} />
    </Box>
  );

  const statusDot = (company: Company) => (
    <StatusDot active={company.isactive} activeLabel={t('common.active', 'Active')} inactiveLabel={t('common.inactive', 'Inactive')} />
  );

  const actionsFor = (company: Company) => {
    const view: RowAction = {
      key: 'view',
      label: t('users.viewDetails', 'View Details'),
      icon: <ViewIcon fontSize="small" />,
      onClick: () => handleView(company.companyid),
    };
    const users: RowAction = {
      key: 'users',
      label: t('companies.viewUsers', 'View Users'),
      icon: <PeopleIcon fontSize="small" />,
      onClick: () => handleViewUsers(company.companyid),
    };
    const edit: RowAction = {
      key: 'edit',
      label: t('common.edit', 'Edit'),
      icon: <EditIcon fontSize="small" />,
      onClick: () => handleEdit(company.companyid),
    };
    // The backend soft-deletes (deactivates) the company
    const remove: RowAction = {
      key: 'delete',
      label: t('common.delete', 'Delete'),
      icon: <DeleteIcon fontSize="small" />,
      color: 'error',
      onClick: () => setDeleteTarget(company),
    };
    return { menu: [view, users, edit, remove], strip: [view, users, edit] };
  };

  const mono = (value?: string) =>
    value ? <Box component="span" sx={{ fontFamily: 'monospace' }}>{value}</Box> : null;

  const detailStrip = (company: Company) => (
    <DetailStrip
      fields={[
        { label: t('companyView.email', 'Email'), value: company.email },
        { label: t('companyView.phone', 'Phone'), value: company.phone },
        {
          label: t('companyView.website', 'Website'),
          value: company.website && (
            <Link href={company.website} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>
              {company.website.replace(/^https?:\/\//, '')}
            </Link>
          ),
        },
        { label: t('companies.reportCode', 'Report Code'), value: mono(company.reportprefix) },
        { label: t('companies.reportNo', 'Report No.'), value: mono(company.reportno) },
      ]}
      description={company.address?.trim() || company.timezone || null}
      actions={actionsFor(company).strip}
    />
  );

  const emptyState = (
    <EmptyState
      title={t('companies.noCompanies')}
      description={t('companies.noCompaniesDescription')}
      icon={<SearchIcon />}
      actionLabel={t('companies.addCompany')}
      onAction={handleAddCompany}
    />
  );

  const paginationProps = {
    rowsPerPageOptions: [10, 25, 50, 100],
    component: 'div' as const,
    count: filteredCompanies.length,
    rowsPerPage,
    page: currentPage,
    onPageChange: (_: unknown, newPage: number) => setPage(newPage),
    onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setRowsPerPage(parseInt(e.target.value, 10));
      setPage(0);
    },
  };

  if (!userIsSuperAdmin) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('companies.accessDeniedDetailed')}
        </Alert>
      </Box>
    );
  }

  if (loading && companies.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const filterSummary =
    searchText || companyTypeFilter !== 'All' || statusFilter !== 'all'
      ? `Showing ${filteredCompanies.length} of ${companies.length} companies (filtered)`
      : `Total companies: ${companies.length}`;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <ListToolbar
        search={searchText}
        onSearch={setSearchText}
        searchPlaceholder={t('companies.searchPlaceholder')}
        statusOptions={[
          { key: 'all', label: t('companies.allCompanies', 'All Companies'), count: searchedCompanies.length },
          { key: 'active', label: t('common.active', 'Active'), count: activeCount },
          { key: 'inactive', label: t('common.inactive', 'Inactive'), count: searchedCompanies.length - activeCount },
        ]}
        status={statusFilter}
        onStatus={setStatusFilter}
        onRefresh={fetchCompanies}
        loading={loading}
        addLabel={t('companies.addCompany')}
        onAdd={handleAddCompany}
        filters={
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="company-type-filter-label">{t('companies.companyType')}</InputLabel>
            <Select
              labelId="company-type-filter-label"
              value={companyTypeFilter}
              label={t('companies.companyType')}
              onChange={handleCompanyTypeFilterChange}
            >
              <MenuItem value="All">{t('common.all')}</MenuItem>
              <MenuItem value="01">{t('companies.superAdmin')}</MenuItem>
              <MenuItem value="02">{t('companies.largeClient')}</MenuItem>
              <MenuItem value="03">{t('companies.mediumClient')}</MenuItem>
              <MenuItem value="04">{t('companies.smallClient')}</MenuItem>
            </Select>
          </FormControl>
        }
      />

      {/* Mobile card list */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {paginatedCompanies.length === 0 ? (
            <Paper sx={{ p: 3 }}>{emptyState}</Paper>
          ) : (
            paginatedCompanies.map((company) => (
              <SelectableCard
                key={company.companyid}
                rowRef={selection.rowRef(company.companyid)}
                selected={selection.selectedId === company.companyid}
                onClick={() => selection.toggle(company.companyid)}
                header={
                  <>
                    <Box sx={{ flex: 1, minWidth: 0 }}>{nameCell(company, 38)}</Box>
                    {statusDot(company)}
                    <RowActionsMenu actions={actionsFor(company).menu} />
                  </>
                }
                body={
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {typeChip(company)}
                    {company.prefixword && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontFamily: 'monospace' }}>
                        {company.prefixword}
                      </Typography>
                    )}
                  </Box>
                }
                detail={detailStrip(company)}
              />
            ))
          )}
          <TablePagination
            {...paginationProps}
            labelRowsPerPage=""
            sx={{ '& .MuiTablePagination-selectLabel': { display: 'none' } }}
          />
        </Box>
      ) : (
        /* Desktop table: click a row to select it and show its detail strip */
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={HEADER_CELL_SX}>{t('companies.companyName', 'Company')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('companies.companyType', 'Type')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('companies.clientCode', 'Client Code')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('companies.timezone', 'Timezone')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 110 }}>{t('common.status', 'Status')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCompanies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    {loading ? (
                      <Typography color="text.secondary">{t('common.loading')}</Typography>
                    ) : (
                      emptyState
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCompanies.map((company) => (
                  <SelectableTableRow
                    key={company.companyid}
                    rowRef={selection.rowRef(company.companyid)}
                    selected={selection.selectedId === company.companyid}
                    onClick={() => selection.toggle(company.companyid)}
                    colSpan={6}
                    detail={detailStrip(company)}
                  >
                    <TableCell sx={{ maxWidth: 360 }}>{nameCell(company)}</TableCell>
                    <TableCell>{typeChip(company)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: company.prefixword ? 'text.primary' : 'text.disabled' }}>
                        {company.prefixword || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={company.timezone || ''}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: company.timezone ? 'text.primary' : 'text.disabled' }}>
                          {getTimezoneAbbreviation(company.timezone)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{statusDot(company)}</TableCell>
                    <TableCell align="right" sx={{ pr: 1 }}>
                      <RowActionsMenu actions={actionsFor(company).menu} />
                    </TableCell>
                  </SelectableTableRow>
                ))
              )}
            </TableBody>
          </Table>
          {/* Footer: filter summary on the left, pagination on the right */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {filterSummary}
              {companyTypeFilter !== 'All' && (
                <span> &bull; {t('companies.companyType')}: {getCompanyTypeLabel(t, companyTypeFilter)}</span>
              )}
            </Typography>
            <TablePagination {...paginationProps} labelRowsPerPage={t('common.rowsPerPage')} />
          </Box>
        </TableContainer>
      )}

      {/* Delete confirmation dialog. The backend soft-deletes (deactivates)
          the company rather than removing data. */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={deleting}
      >
        <DialogTitle>{t('companies.deleteConfirmTitle', 'Delete company?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('companies.deleteConfirmBody', {
              defaultValue:
                '"{{name}}" will be deactivated and hidden from active lists. Its projects, pours and users are retained.',
              name: deleteTarget?.companyname || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? t('companies.deleting', 'Deleting...') : t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar !== null} autoHideDuration={4000} onClose={() => setSnackbar(null)}>
        <Alert
          severity={snackbar?.severity ?? 'success'}
          onClose={() => setSnackbar(null)}
          variant="filled"
        >
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Companies;
